import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useMatch } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { PageBackground } from "../../components/PageBackground";
import { countOpenExceptions } from "../../services/qboExceptionsService";

// /support/quickbooks: one page for the Stripe to QuickBooks connector, reached
// from Admin Tools on /support. Its tabs are the connection (index), the open
// exceptions, the invoice export and the billing reconciliation. The tabs share the open exceptions count
// through the outlet context. The page counts again on every tab change, except
// on the Exceptions tab, which reads the exact count with its rows and reports
// it; leaving a tab drops its pending count, so a late answer never overwrites
// a newer number. A failed count is passed on (countFailed) rather than hidden.

const TABS = [
  { to: "/support/quickbooks", label: "Connection", end: true },
  { to: "/support/quickbooks/exceptions", label: "Exceptions", counts: true },
  { to: "/support/quickbooks/invoice-export", label: "Invoice export" },
  { to: "/support/quickbooks/reconciliation", label: "Reconciliation" },
];

// Hover and keyboard focus need pseudo-classes, which inline styles cannot express.
const TAB_CSS = `
  .qbo-tab { transition: color 150ms ease-out, border-color 150ms ease-out; }
  .qbo-tab:hover { color: #151515; }
  .qbo-tab:focus-visible { outline: 2px solid #F90091; outline-offset: 3px; border-radius: 4px; }
  @media (prefers-reduced-motion: reduce) { .qbo-tab { transition: none; } }
`;

function tabStyle({ isActive }) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 0 12px",
    marginBottom: -1,
    fontSize: 14,
    fontWeight: 600,
    color: isActive ? "#151515" : "#626262",
    textDecoration: "none",
    borderBottom: `2px solid ${isActive ? "#F90091" : "transparent"}`,
    whiteSpace: "nowrap",
  };
}

export function QuickBooksLayout() {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const onExceptionsTab = Boolean(useMatch("/support/quickbooks/exceptions"));
  const [count, setCount] = useState({ value: null, failed: false });

  const setOpenExceptions = useCallback((n) => setCount({ value: n, failed: false }), []);

  useEffect(() => {
    if (onExceptionsTab) return undefined;
    let live = true;
    countOpenExceptions()
      .then((n) => { if (live) setCount({ value: n, failed: false }); })
      .catch(() => { if (live) setCount({ value: null, failed: true }); });
    return () => { live = false; };
  }, [onExceptionsTab, pathname]);

  const openExceptions = count.value;
  const outletContext = useMemo(
    () => ({ openExceptions, countFailed: count.failed, setOpenExceptions }),
    [openExceptions, count.failed, setOpenExceptions],
  );

  return (
    <div style={{ minHeight: "100vh", background: "#FFFFFF", fontFamily: "'DM Sans','Helvetica Neue',sans-serif", color: "#151515", display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
      <style>{TAB_CSS}</style>
      <PageBackground />
      <div style={{ background: "rgba(255,255,255,0.85)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", borderBottom: "1px solid #dad8d9", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontSize: 10, color: "#F90091", letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600 }}>Internal support</span>
          <span style={{ color: "#626262", fontSize: 13 }}>{user?.email}</span>
        </div>
        <Link to="/support" style={{ fontSize: 13, color: "#626262", textDecoration: "none" }}>← Advertisers</Link>
        <button type="button" onClick={() => signOut()} style={{ padding: "8px 14px", background: "transparent", color: "#626262", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>Sign out</button>
      </div>

      <div style={{ flex: 1, padding: "28px 24px 48px", maxWidth: 900, margin: "0 auto", width: "100%", position: "relative", zIndex: 1, boxSizing: "border-box" }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, color: "#151515", margin: "0 0 6px", fontFamily: "'Clash Display Variable','Clash Display',sans-serif" }}>QuickBooks</h1>
        <p style={{ color: "#626262", fontSize: 14, margin: "0 0 20px", maxWidth: "65ch" }}>
          The company the Stripe connector posts to, the records it could not post, and invoice and reconciliation files for accounting.
        </p>

        <nav aria-label="QuickBooks" style={{ display: "flex", gap: 28, borderBottom: "1px solid #E2E2E2", marginBottom: 24, overflowX: "auto" }}>
          {TABS.map((tab) => {
            const count = tab.counts && openExceptions > 0 ? openExceptions : null;
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className="qbo-tab"
                style={tabStyle}
                aria-label={count !== null ? `${tab.label}, ${count} open` : undefined}
              >
                {tab.label}
                {count !== null && (
                  <span aria-hidden="true" style={{ minWidth: 20, padding: "1px 7px", borderRadius: 999, background: "#FFE9F6", color: "#A3005F", fontSize: 12, fontWeight: 700, textAlign: "center", fontVariantNumeric: "tabular-nums", boxSizing: "border-box" }}>{count}</span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <Outlet context={outletContext} />
      </div>
    </div>
  );
}
